// =============================================================================
// HopViewScreen — /hop-view/:token (PUBLIC, no auth) — HOP Slice 3
//
// Client-facing HOP photo handover for one project. The token validates
// server-side and returns the project + dated progress updates (batches), each
// holding only the curated photos Coretel shared — grouped by category /
// instance / subcategory so the client can see exactly what each photo is.
// Batches are collapsible (photos only load when a section is opened) and a
// lightbox loads full-res on tap — keeps it snappy on big handovers. Its own
// acceptance-platform look, NOT the diary. Rendered outside the AppShell.
// =============================================================================

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Download, Images, Loader2, AlertTriangle, CalendarClock, Building2, MapPin,
  ClipboardCheck, ChevronDown, ChevronRight, X, Layers, FileText, Wrench, Check,
} from "lucide-react";
import { downloadDiaryPhotosZip } from "../diary/diaryZip";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "https://api.coretelgroup.com";

interface Photo { asset_id: string; filename: string; url: string; size_bytes: number | null; category: string | null; instance: string | null; subcat: string | null; }
interface Doc { upload_id: string; type: string; file_name: string; version_label: string | null; size_bytes: number | null; url: string; }
interface Batch { batch_id: string; title: string | null; note: string | null; created_at: string; photos: Photo[]; documents?: Doc[]; }
interface Defect { defect_id: string; defect_number: string; category: string; severity: string; title: string | null; description: string; status: string; due_date: string | null; source: string; can_client_close?: boolean; }
interface ViewData {
  share:   { title: string | null; expires_at: string | null };
  project: { project_code: string; project_name: string; client_name: string | null; site_name: string | null } | null;
  batches: Batch[];
  defects?: Defect[];
}

function fmtDay(iso: string): string {
  try { return new Date(iso).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}
function fmtExpiry(iso: string | null): string {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
}
function safe(s: string): string { return s.replace(/[^A-Za-z0-9]+/g, "_"); }

// Group a batch's photos into category (+ instance) → subcategory sections.
function sectionsFor(photos: Photo[]) {
  const cats = new Map<string, { key: string; category: string; instance: string | null; subs: Map<string, { label: string | null; photos: Photo[] }> }>();
  for (const p of photos) {
    const category = p.category || "Photos";
    const key = `${category}${p.instance ? " · " + p.instance : ""}`;
    if (!cats.has(key)) cats.set(key, { key, category, instance: p.instance, subs: new Map() });
    const c = cats.get(key)!;
    const subKey = p.subcat || "__none__";
    if (!c.subs.has(subKey)) c.subs.set(subKey, { label: p.subcat, photos: [] });
    c.subs.get(subKey)!.photos.push(p);
  }
  return [...cats.values()].map(c => ({
    key: c.key, category: c.category, instance: c.instance,
    subs: [...c.subs.values()],
    total: [...c.subs.values()].reduce((a, s) => a + s.photos.length, 0),
  }));
}

export default function HopViewScreen() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ViewData | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "invalid" | "expired" | "revoked">("loading");
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [lightbox, setLightbox] = useState<Photo | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [rDesc, setRDesc] = useState("");
  const [rCat, setRCat] = useState("other");
  const [rContact, setRContact] = useState("");
  const [rBusy, setRBusy] = useState(false);
  const [rDone, setRDone] = useState(false);
  // Client-side defect view + confirm-close
  const [openDefect, setOpenDefect] = useState<string | null>(null);
  const [closeNote, setCloseNote] = useState("");
  const [closeContact, setCloseContact] = useState("");
  const [closeBusy, setCloseBusy] = useState(false);
  const [closedIds, setClosedIds] = useState<Set<string>>(new Set());

  async function closeDefect(d: Defect) {
    setCloseBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/hop-view/${token}/defect/${d.defect_id}/close`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: closeNote.trim(), contact: closeContact.trim() }),
      });
      if (res.ok) {
        setClosedIds(prev => new Set(prev).add(d.defect_id));
        setOpenDefect(null); setCloseNote(""); setCloseContact("");
      } else {
        alert("Couldn't close this — it may have changed status. Refresh and try again.");
      }
    } catch { alert("Couldn't reach the server — please try again."); }
    finally { setCloseBusy(false); }
  }

  async function submitDefect() {
    if (!rDesc.trim()) return;
    setRBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/hop-view/${token}/defect`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ description: rDesc.trim(), category: rCat, contact: rContact.trim() }),
      });
      if (res.ok) { setRDone(true); setReportOpen(false); setRDesc(""); setRContact(""); }
    } catch { /* noop */ } finally { setRBusy(false); }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/hop-view/${token}`);
        if (cancelled) return;
        if (res.status === 410) { const b = await res.json().catch(() => ({})); setState(b?.error === "revoked" ? "revoked" : "expired"); return; }
        if (!res.ok) { setState("invalid"); return; }
        const json = (await res.json()) as ViewData;
        setData(json); setState("ok");
        if (json.batches?.[0]) setOpen(new Set([json.batches[0].batch_id]));   // newest open
      } catch { if (!cancelled) setState("invalid"); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const totalPhotos = useMemo(() => (data?.batches ?? []).reduce((a, b) => a + b.photos.length, 0), [data]);

  async function handleBatchZip(b: Batch) {
    if (b.photos.length === 0) return;
    const base = `${safe(data?.project?.project_code ?? "site")}_${safe(b.title || fmtDay(b.created_at))}`;
    setBusy("batch-" + b.batch_id);
    try {
      const r = await downloadDiaryPhotosZip({ filenameBase: base, photos: b.photos.map(p => ({ url: p.url, name: p.filename })) });
      if (r.failed.length > 0) alert(`${r.zipped} of ${r.total} photos downloaded — ${r.failed.length} failed (see _MISSING_PHOTOS.txt in the zip). Run it again for a complete set.`);
    } catch { alert("Download failed — please try again."); } finally { setBusy(null); }
  }
  async function handleDownloadAll() {
    if (!data) return;
    const photos: { url: string; name: string }[] = [];
    for (const b of data.batches) { const folder = safe(b.title || fmtDay(b.created_at)); for (const p of b.photos) photos.push({ url: p.url, name: `${folder}/${p.filename}` }); }
    if (photos.length === 0) return;
    setBusy("all");
    try {
      const r = await downloadDiaryPhotosZip({ filenameBase: `${safe(data.project?.project_code ?? "site")}_HOP_AllPhotos`, photos });
      if (r.failed.length > 0) alert(`${r.zipped} of ${r.total} photos downloaded — ${r.failed.length} failed (see _MISSING_PHOTOS.txt in the zip). Run it again for a complete set.`);
    }
    catch { alert("Download failed — please try again."); } finally { setBusy(null); }
  }

  if (state === "loading") return <div className="min-h-screen flex items-center justify-center bg-bg"><Loader2 className="animate-spin text-accent" size={28} /></div>;
  if (state !== "ok" || !data) {
    const msg = state === "expired" ? "This link has expired." : state === "revoked" ? "This link has been replaced by a newer one." : "This link isn't valid.";
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-bg text-center p-6">
        <AlertTriangle size={32} className="text-accent mb-3" />
        <div className="text-[16px] font-semibold text-text">{msg}</div>
        <div className="text-[13px] text-text-muted mt-1">Ask your Coretel contact for a fresh link.</div>
      </div>
    );
  }

  const title = data.share.title ?? `${data.project?.project_code ?? ""} — HOP Photo Handover`;

  return (
    <div className="min-h-screen bg-bg text-text">
      {/* Header — acceptance-platform band */}
      <div className="bg-gradient-to-br from-accent to-accent-hover text-white">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-8 md:py-10">
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider2 text-white/85">
            <ClipboardCheck size={13} /> Coretel · HOP Photo Handover
          </div>
          <div className="text-2xl md:text-[28px] font-semibold mt-1.5">{title}</div>
          <div className="text-[13px] text-white/90 mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
            {data.project?.project_name && <span>{data.project.project_name}</span>}
            {data.project?.client_name && <span className="inline-flex items-center gap-1.5"><Building2 size={13} /> {data.project.client_name}</span>}
            {data.project?.site_name && <span className="inline-flex items-center gap-1.5"><MapPin size={13} /> {data.project.site_name}</span>}
          </div>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <div className="bg-white/15 rounded-md px-3 py-1.5 text-[12px] inline-flex items-center gap-2">
              <Layers size={13} /> {data.batches.length} update{data.batches.length === 1 ? "" : "s"} · {totalPhotos} photo{totalPhotos === 1 ? "" : "s"}
            </div>
            {data.share.expires_at && <div className="text-[12px] text-white/80 inline-flex items-center gap-1"><CalendarClock size={12} /> available until {fmtExpiry(data.share.expires_at)}</div>}
            {totalPhotos > 0 && (
              <button onClick={handleDownloadAll} disabled={busy !== null}
                className="ml-auto inline-flex items-center gap-2 bg-white text-accent px-3.5 py-2 rounded-md text-[13px] font-semibold disabled:opacity-60 hover:bg-white/90">
                <Images size={15} /> {busy === "all" ? "Zipping…" : "Download everything"}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-3">
        {/* Defects & issues — open items + client report */}
        <div className="bg-ink-card border border-line rounded-lg p-4 md:p-5">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[14px] font-semibold text-text inline-flex items-center gap-2">
              <Wrench size={15} className="text-accent" /> Defects &amp; issues
              {(data.defects?.length ?? 0) > 0 && <span className="text-[11px] text-text-muted">· {data.defects!.length} open</span>}
            </div>
            {rDone
              ? <span className="text-[12px] text-ok inline-flex items-center gap-1"><Check size={13} /> Reported — thanks</span>
              : <button onClick={() => setReportOpen(v => !v)} className="text-[12px] text-accent hover:underline">{reportOpen ? "Cancel" : "Report an issue"}</button>}
          </div>

          {(data.defects?.filter(d => !closedIds.has(d.defect_id)).length ?? 0) > 0 && (
            <div className="space-y-1.5 mt-3">
              {data.defects!.filter(d => !closedIds.has(d.defect_id)).map(d => {
                const isOpenD = openDefect === d.defect_id;
                return (
                <div key={d.defect_id} className="rounded-md border border-line bg-ink-raised text-[12px] overflow-hidden">
                  <button type="button" onClick={() => setOpenDefect(isOpenD ? null : d.defect_id)} className="w-full text-left px-3 py-2 hover:bg-white/5">
                    <div className="flex items-center gap-2 flex-wrap text-text-muted">
                      <span className="font-mono text-accent">{d.defect_number}</span>
                      <span className="capitalize">{d.category} · {d.severity}</span>
                      <span>· {d.status.replace(/_/g, " ")}</span>
                      {d.can_client_close && <span className="text-[10px] text-emerald-500 bg-emerald-500/10 rounded px-1.5 py-0.5 font-medium">ready to confirm</span>}
                      {d.due_date && <span className="ml-auto">ETA {d.due_date}</span>}
                    </div>
                    <div className="text-text mt-0.5">{d.title || d.description}</div>
                  </button>
                  {isOpenD && (
                    <div className="px-3 pb-3 pt-1 border-t border-line space-y-2">
                      {d.title && <div className="text-text-muted whitespace-pre-wrap">{d.description}</div>}
                      {d.can_client_close ? (
                        <div className="space-y-2 pt-1">
                          <div className="text-[12px] text-text">We've marked this fixed and ready for you to check. Happy it's resolved? Confirm to close it off.</div>
                          <input value={closeContact} onChange={e => setCloseContact(e.target.value)} placeholder="Your name (optional)"
                            className="w-full px-2.5 py-1.5 bg-ink border border-line rounded text-[13px] text-text" />
                          <textarea value={closeNote} onChange={e => setCloseNote(e.target.value)} rows={2} placeholder="Any note (optional)"
                            className="w-full px-2.5 py-1.5 bg-ink border border-line rounded text-[13px] text-text" />
                          <div className="flex justify-end">
                            <button onClick={() => closeDefect(d)} disabled={closeBusy}
                              className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-4 py-2 rounded-md text-[13px] font-medium disabled:opacity-60 hover:bg-emerald-500">
                              <Check size={14} /> {closeBusy ? "Closing…" : "Confirm fixed & close"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-[11px] text-text-muted pt-1">Coretel is working on this. You'll be able to confirm it here once it's marked ready.</div>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
          {(data.defects?.filter(d => !closedIds.has(d.defect_id)).length ?? 0) === 0 && !reportOpen && !rDone && (
            <div className="text-[12px] text-text-muted mt-1">No open issues logged. Spotted something? Tap “Report an issue”.</div>
          )}

          {reportOpen && (
            <div className="mt-3 space-y-2">
              <textarea value={rDesc} onChange={e => setRDesc(e.target.value)} rows={3} placeholder="Describe the issue — what and where."
                className="w-full px-3 py-2 bg-ink border border-line rounded text-[13px] text-text outline-none focus:border-accent" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <select value={rCat} onChange={e => setRCat(e.target.value)} className="bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text capitalize">
                  {["gland", "ground", "rigging", "civil", "structural", "electrical", "other"].map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
                </select>
                <input value={rContact} onChange={e => setRContact(e.target.value)} placeholder="Your name / email (optional)"
                  className="bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text" />
              </div>
              <div className="flex justify-end">
                <button onClick={submitDefect} disabled={rBusy || !rDesc.trim()}
                  className="inline-flex items-center gap-1.5 bg-accent text-white px-4 py-2 rounded-md text-[13px] font-medium disabled:opacity-60 hover:bg-accent-hover">{rBusy ? "Sending…" : "Submit issue"}</button>
              </div>
            </div>
          )}
        </div>

        {data.batches.length === 0 && <div className="text-center text-text-muted text-[13px] py-16">No photos shared yet — check back soon.</div>}

        {data.batches.map(b => {
          const isOpen = open.has(b.batch_id);
          const sections = sectionsFor(b.photos);
          return (
            <div key={b.batch_id} className="bg-ink-card border border-line rounded-lg overflow-hidden">
              {/* Batch header (click to expand) */}
              <div className="w-full flex items-center gap-3 p-4 md:p-5">
                <button onClick={() => setOpen(prev => { const n = new Set(prev); n.has(b.batch_id) ? n.delete(b.batch_id) : n.add(b.batch_id); return n; })}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left">
                  {isOpen ? <ChevronDown size={18} className="text-accent flex-shrink-0" /> : <ChevronRight size={18} className="text-text-muted flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-semibold text-text truncate">{b.title || "Progress update"}</div>
                    <div className="text-[11px] text-text-muted mt-0.5">
                      {fmtDay(b.created_at)}
                      {b.photos.length > 0 ? ` · ${b.photos.length} photo${b.photos.length === 1 ? "" : "s"}` : ""}
                      {(b.documents?.length ?? 0) > 0 ? ` · ${b.documents!.length} doc${b.documents!.length === 1 ? "" : "s"}` : ""}
                    </div>
                  </div>
                </button>
                {b.photos.length > 0 && (
                  <button type="button" onClick={() => handleBatchZip(b)} disabled={busy !== null}
                    className="inline-flex items-center gap-1.5 bg-ink border border-line px-3 py-1.5 rounded text-[12px] font-medium hover:border-accent flex-shrink-0 disabled:opacity-60">
                    <Images size={13} /> {busy === "batch-" + b.batch_id ? "Zipping…" : "Download"}
                  </button>
                )}
              </div>

              {/* Expanded content — sections by category/instance */}
              {isOpen && (
                <div className="px-4 md:px-5 pb-5 space-y-5 border-t border-line pt-4">
                  {b.note && <div className="text-[13px] text-text-muted whitespace-pre-wrap -mt-1">{b.note}</div>}

                  {/* Documents */}
                  {(b.documents?.length ?? 0) > 0 && (
                    <div>
                      <div className="flex items-baseline gap-2 mb-2 pl-2 border-l-2 border-accent">
                        <span className="text-[13px] font-semibold text-text">Documents</span>
                        <span className="text-[11px] text-text-muted">· {b.documents!.length}</span>
                      </div>
                      <div className="space-y-1.5">
                        {b.documents!.map(d => (
                          <a key={d.upload_id} href={d.url} download target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-3 px-3 py-2 rounded-md border border-line bg-ink-raised hover:border-accent transition-colors">
                            <FileText size={15} className="text-accent flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="text-[13px] text-text truncate">{d.type}{d.version_label ? ` · ${d.version_label}` : ""}</div>
                              <div className="text-[11px] text-text-muted font-mono truncate">{d.file_name}</div>
                            </div>
                            <Download size={14} className="text-text-muted flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {sections.map(sec => (
                    <div key={sec.key}>
                      <div className="flex items-baseline gap-2 mb-2 pl-2 border-l-2 border-accent">
                        <span className="text-[13px] font-semibold text-text">{sec.category}</span>
                        {sec.instance && <span className="text-[11px] font-mono text-accent bg-accent/10 rounded px-1.5 py-0.5">{sec.instance}</span>}
                        <span className="text-[11px] text-text-muted">· {sec.total}</span>
                      </div>
                      <div className="space-y-3">
                        {sec.subs.map((sub, si) => (
                          <div key={si}>
                            {sub.label && <div className="text-[11px] text-text-muted font-medium mb-1.5 pl-2.5">{sub.label} <span className="text-text-dim">· {sub.photos.length}</span></div>}
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                              {sub.photos.map(p => (
                                <button key={p.asset_id} onClick={() => setLightbox(p)}
                                  className="block aspect-square bg-ink-raised border border-line rounded overflow-hidden hover:border-accent transition-colors relative text-left">
                                  <img src={p.url} alt={p.subcat ?? ""} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div className="mt-8 pt-6 border-t border-line text-[11px] text-text-dim text-center">Coretel Group Pty Ltd · ops.coretelgroup.com</div>
      </div>

      {/* Lightbox — full-res on demand */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" onClick={() => setLightbox(null)}>
          <div className="flex items-center justify-between px-4 py-3 text-white/90" onClick={e => e.stopPropagation()}>
            <div className="min-w-0">
              <div className="text-[13px] font-mono truncate">{lightbox.filename}</div>
              <div className="text-[11px] text-white/60">{[lightbox.category, lightbox.instance, lightbox.subcat].filter(Boolean).join(" · ")}</div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <a href={lightbox.url} download className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded px-3 py-1.5 text-[13px]"><Download size={14} /> Download</a>
              <button onClick={() => setLightbox(null)} className="p-1.5 hover:bg-white/15 rounded"><X size={20} /></button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center p-4 overflow-hidden" onClick={() => setLightbox(null)}>
            <img src={lightbox.url} alt="" className="max-w-full max-h-full object-contain" />
          </div>
        </div>
      )}
    </div>
  );
}
